import { Injectable, computed, inject, signal } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { GameAttrType, GameCategoryType } from '../../types/game.type';
import { LanguageService } from '../language.service';
import { EnvironmentService } from '../environment.service';
import { GameCategoryBlockType } from '../../types/home.type';
import { environment } from 'src/environments/environment';
import { HostRequestsInterceptor } from '../../interceptors/host-requests.interceptor';
import { GAME_LIST_REQUEST_LIMIT } from '../../enums/game-config.enum';

@Injectable({
  providedIn: 'root',
})
export class GameCategoryAttrService {

  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _lang = inject(LanguageService);
  private _env = inject(EnvironmentService);
  private _hostInterceptor = inject(HostRequestsInterceptor);

  /**
   * Game category list observable
   */
  public categoryList$: ReplaySubject<GameCategoryType[]> = new ReplaySubject<GameCategoryType[]>(1);

  /**
   * Game category list
   */
  public categoryList = signal<GameCategoryType[]>([]);

  /**
   * Game category list for filter
   */
  public filterCategoryList = computed(() => {
    return this.categoryList()
      .sort((a, b) => a?.name?.toUpperCase() < b?.name?.toUpperCase() ? -1 : a?.name?.toUpperCase() > b?.name?.toUpperCase() ? 1 : 0);
  });

  /**
   * Game attr list observable
   */
  public attrList$: ReplaySubject<GameAttrType[]> = new ReplaySubject<GameAttrType[]>(1);

  /**
   * Game attr list
   */
  public attrList = signal<GameAttrType[]>([]);

  /**
   * Game attr list for filter
   */
  public filterAttrList = computed(() => {
    return this.attrList()
      .sort((a, b) => a?.name?.toUpperCase() < b?.name?.toUpperCase() ? -1 : a?.name?.toUpperCase() > b?.name?.toUpperCase() ? 1 : 0);
  });

  /**
   * Game category & attr list observable
   */
  public categoryAttrList$: ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }> = new ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }>(1);

  /**
   * Subscribes to language change events and reloads category and attribute lists
   * Ensures category and attribute data is updated when the language changes
   */
  public updateCategoryAttrListOnLangChange() {
    this._lang.langChange$.subscribe(() => {
      this.loadCategoryAttrList();
    });
  }

  /**
   * Loads game category and attribute lists from the API
   * Retrieves list with optional country parameter for authenticated users
   * Updates local signals and replay subjects with fetched data
   * Converts category and attribute IDs to numbers and sets additional properties
   */
  public loadCategoryAttrList(): void {
    const params = this._user.auth ? { country: this._user?.info?.country || this._env.dataEnv().country?.short } : {};
    this._api.gameCategoryAttrList(params).pipe(
      first(),
      map(response => response.data),
      map(({ categoryList, attributeList }) => ({
        categoryList: categoryList.map(c => ({ ...c, id: +c.id, imgSrc: this._resolveImg(c?.imgSrc) })),
        attributeList: attributeList.map(a => ({ ...a, id: +a.id, slug: a.short?.toLowerCase()?.replace(' ', '-'), imgSrc: this._resolveImg(a?.imgSrc) })),
      })),
      tap(({ categoryList, attributeList }) => {
        this.categoryList.set(categoryList);
        this.attrList.set(attributeList);

        this.categoryList$.next(categoryList);
        this.attrList$.next(attributeList);
        this.categoryAttrList$.next({ categoryList, attributeList });
      }),
    ).subscribe();
  }

  /**
   * Returns category by id
   * @param id
   */
  public getCategoryById(id): GameCategoryType {
    return this.categoryList().find(category => category.id === +id);
  }

  /**
   * Returns category by name
   * @param name
   */
  public getCategoryByAlias(name: string): GameCategoryType {
    name = name?.toLowerCase()?.replace(' ', '-');
    return this.categoryList().find(category => category.slug === name);
  }

  /**
   * Returns attr by id
   * @param id
   */
  public getAttrById(id: string | number): GameAttrType {
    return this.attrList().find(attr => attr.id === +id);
  }

  /**
   * Returns attr by name
   * @param name
   */
  public getAttrByAlias(name: string): GameAttrType {
    name = name?.toLowerCase()?.replace(' ', '-');
    return this.attrList().find(attr => attr.slug === name);
  }

  public createGameListConfig(data: GameCategoryBlockType, gamesUrl: string = 'casino') {
    const attribute = this.getAttrByAlias(data.slug);
    let filter: any = {
      limit: GAME_LIST_REQUEST_LIMIT
    };
    if (attribute) {
      filter['attribute'] = attribute.id;
    }
    return {
      ...data,
      link: `/${gamesUrl}/${data.slug}`,
      filter,
      imgSrc: attribute?.imgSrc || null
    }
  }

  private _resolveImg(value: string | null) {
    if (value) {
      return this._hostInterceptor.overrideUrl(environment.assets_host + value);
    }
    return value;
  }

}
